import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Empty, Input, Select, Spin, Tag, Tooltip } from "antd";
import {
  ArrowDownOutlined, ArrowUpOutlined, MailOutlined, PaperClipOutlined, RollbackOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import * as api from "./controller";
import EmailBody from "./EmailBody";
import { EmailComposeModal } from "./EmailComposeModal";
import "./emailFeed.css";

// Today: 14:05 · this year: Oct 3 · older: Oct 3, 2025.
export function feedTime(iso, now = dayjs()) {
  if (!iso) return "";
  const d = dayjs(iso);
  if (d.isSame(now, "day")) return d.format("HH:mm");
  if (d.isSame(now, "year")) return d.format("MMM D");
  return d.format("MMM D, YYYY");
}

const person = (p) => (p ? p.name || p.address : "");
const people = (list) => (list || []).map(person).filter(Boolean);

function recipientsLine(email) {
  const all = people(email.to).concat(people(email.cc));
  if (!all.length) return "";
  return all.length > 2 ? `${all.slice(0, 2).join(", ")} +${all.length - 2}` : all.join(", ");
}

function Header({ label, list }) {
  const shown = (list || []).filter((p) => p && p.address);
  if (!shown.length) return null;
  return (
    <div className="emf-hdr">
      <span className="emf-hdr-k">{label}</span>
      <span className="emf-hdr-v">
        {shown.map((p) => (p.name ? `${p.name} <${p.address}>` : p.address)).join(", ")}
      </span>
    </div>
  );
}

/**
 * Every email for an office or a lead, newest first — no threads. Click an email
 * to read it in place; Reply / Reply all answer THAT email.
 *
 * Props: `officeId` or `leadId`; the address book for New email (`contacts`,
 * `officeEmail`, `practice`, `tracked`, or `leadEmail`); `initialMessageId` to
 * open one email straight away (the Today tab's "Open").
 */
export default function EmailFeed({
  officeId, leadId, contacts, officeEmail, practice, tracked, leadEmail, initialMessageId, onSent,
}) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [inboxes, setInboxes] = useState([]);
  const [inbox, setInbox] = useState("");
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState({});        // id → true
  const [details, setDetails] = useState({});  // id → full email (body, attachments)
  const [pinned, setPinned] = useState(null);  // the deep-linked email, when older than page 1
  const [compose, setCompose] = useState(null); // {mode: "new"} | {mode: "reply", email, all}
  const rowRefs = useRef({});

  const fetchPage = useCallback((n) => {
    const opts = { page: n, mailbox: inbox, q: applied };
    return leadId ? api.getLeadEmails(leadId, opts) : api.getOfficeEmails(officeId, opts);
  }, [officeId, leadId, inbox, applied]);

  const load = useCallback(async (n = 1) => {
    if (n === 1) setLoading(true);
    const r = await fetchPage(n);
    const ok = r.status >= 200 && r.status < 300 && r.json && Array.isArray(r.json.results);
    setFailed(!ok);
    if (ok) {
      setRows((prev) => (n === 1 ? r.json.results : prev.concat(r.json.results)));
      setCount(r.json.count || 0);
      setPage(n);
      if (Array.isArray(r.json.inboxes)) setInboxes(r.json.inboxes);
    }
    setLoading(false);
  }, [fetchPage]);
  useEffect(() => { load(1); }, [load]);

  const openEmail = useCallback(async (id, { scroll = false } = {}) => {
    setOpen((o) => ({ ...o, [id]: true }));
    if (!details[id]) {
      const r = await api.getEmail(id);
      if (r.status >= 200 && r.status < 300 && r.json) {
        setDetails((d) => ({ ...d, [id]: r.json }));
      }
    }
    if (scroll && rowRefs.current[id] && rowRefs.current[id].scrollIntoView) {
      rowRefs.current[id].scrollIntoView({ block: "nearest" });
    }
  }, [details]);

  const toggle = (id) => {
    if (open[id]) setOpen((o) => ({ ...o, [id]: false }));
    else openEmail(id);
  };

  // The Today tab's "Open": land on that email once the list is in — fetched on
  // its own and pinned on top when it is older than the first page.
  const deepLinked = useRef(null);
  useEffect(() => {
    if (!initialMessageId || loading || deepLinked.current === initialMessageId) return;
    deepLinked.current = initialMessageId;
    const id = Number(initialMessageId);
    if (!rows.some((r) => r.id === id)) {
      api.getEmail(id).then((r) => {
        if (r.status >= 200 && r.status < 300 && r.json) {
          setPinned(r.json);
          setDetails((d) => ({ ...d, [id]: r.json }));
          setOpen((o) => ({ ...o, [id]: true }));
        }
      });
    } else {
      openEmail(id, { scroll: true });
    }
  }, [initialMessageId, loading, rows, openEmail]);

  const sent = () => {
    setCompose(null);
    load(1);
    onSent && onSent();
  };

  const list = pinned && !rows.some((r) => r.id === pinned.id) ? [pinned].concat(rows) : rows;

  const renderRow = (email) => {
    const isOpen = !!open[email.id];
    const full = details[email.id];
    const out = email.direction === "outbound";
    const isPinned = pinned && pinned.id === email.id && !rows.some((r) => r.id === email.id);
    return (
      <div
        key={email.id}
        ref={(el) => { rowRefs.current[email.id] = el; }}
        className={`emf-item${isOpen ? " is-open" : ""}${out ? " is-out" : ""}`}
      >
        <button type="button" className="emf-row" onClick={() => toggle(email.id)} aria-expanded={isOpen}>
          <span className={`emf-dir ${out ? "out" : "in"}`} aria-label={out ? "Sent" : "Received"}>
            {out ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          </span>
          <span className="emf-main">
            <span className="emf-line1">
              <span className="emf-who">{person(email.from) || "(unknown sender)"}</span>
              {recipientsLine(email) ? <span className="emf-to">to {recipientsLine(email)}</span> : null}
              <span className="emf-time" title={email.at ? dayjs(email.at).format("MMM D, YYYY HH:mm") : ""}>
                {feedTime(email.at)}
              </span>
            </span>
            <span className="emf-line2">
              <span className="emf-subject">{email.subject || "(no subject)"}</span>
              {!isOpen && email.preview ? <span className="emf-preview"> — {email.preview}</span> : null}
            </span>
            <span className="emf-chips">
              {isPinned ? <Tag color="purple" className="emf-tag">Opened from Today</Tag> : null}
              {email.mailbox ? (
                <span className={`emf-inbox${email.mailbox.kind === "ftc" ? " is-ftc" : ""}`}>{email.mailbox.address}</span>
              ) : null}
              {email.is_reminder ? <Tag color="gold" className="emf-tag">reminder</Tag> : null}
              {email.has_attachments ? <PaperClipOutlined className="emf-clip" /> : null}
            </span>
          </span>
        </button>
        {isOpen ? (
          <div className="emf-open">
            <div className="emf-hdrs">
              <Header label="From" list={[email.from]} />
              <Header label="To" list={email.to} />
              <Header label="Cc" list={email.cc} />
              <div className="emf-hdr">
                <span className="emf-hdr-k">Date</span>
                <span className="emf-hdr-v">
                  {email.at ? dayjs(email.at).format("ddd, MMM D, YYYY · HH:mm") : "—"}
                  {email.mailbox ? <span className="emf-via"> · via {email.mailbox.address}</span> : null}
                </span>
              </div>
            </div>
            <div className="emf-body">
              {full ? <EmailBody html={full.body_html} preview={email.preview} /> : <Spin size="small" />}
            </div>
            {full && (full.attachments || []).length ? (
              <div className="emf-atts">
                {full.attachments.map((a) => (
                  <a key={a.id} href={a.download_url} target="_blank" rel="noreferrer" className="emf-att">
                    <PaperClipOutlined /> {a.filename}
                  </a>
                ))}
              </div>
            ) : null}
            <div className="emf-actions">
              <Tooltip title={email.can_reply ? "" : "This email can't be replied to from here."}>
                <Button size="small" icon={<RollbackOutlined />} disabled={!email.can_reply}
                        onClick={() => setCompose({ mode: "reply", email, all: false })}>
                  Reply
                </Button>
              </Tooltip>
              <Button size="small" disabled={!email.can_reply}
                      onClick={() => setCompose({ mode: "reply", email, all: true })}>
                Reply all
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="emf">
      <div className="emf-bar">
        {inboxes.length > 1 ? (
          <Select
            size="small"
            value={inbox}
            onChange={(v) => { setInbox(v); setPinned(null); }}
            options={[{ value: "", label: "All inboxes" }].concat(inboxes)}
            // The drawer sits at z-index 10001; popups default below it.
            dropdownStyle={{ zIndex: 10050 }}
            style={{ minWidth: 190 }}
          />
        ) : null}
        <Input.Search
          size="small"
          allowClear
          placeholder="Search subject, sender, address"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (!e.target.value) setApplied(""); }}
          onSearch={(v) => setApplied((v || "").trim())}
          className="emf-search"
        />
        <span className="emf-bar-right">
          <Button size="small" onClick={() => load(1)}>Refresh</Button>
          <Button size="small" type="primary" icon={<MailOutlined />} onClick={() => setCompose({ mode: "new" })}>
            New email
          </Button>
        </span>
      </div>

      {loading ? (
        <div className="emf-loading"><Spin /></div>
      ) : failed ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={(
          <>
            <div>Couldn&rsquo;t load the emails.</div>
            <Button size="small" style={{ marginTop: 10 }} onClick={() => load(1)}>Try again</Button>
          </>
        )} />
      ) : list.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
               description={applied || inbox ? "No emails match." : "No emails yet."} />
      ) : (
        <div className="emf-list">
          {list.map(renderRow)}
          {rows.length < count ? (
            <div className="emf-more">
              <Button size="small" onClick={() => load(page + 1)}>
                Load older ({count - rows.length} more)
              </Button>
            </div>
          ) : null}
        </div>
      )}

      <EmailComposeModal
        open={!!compose}
        onClose={() => setCompose(null)}
        onSent={sent}
        officeId={officeId}
        leadId={leadId}
        defaultTo={tracked}
        contacts={contacts}
        officeEmail={officeEmail}
        practice={practice}
        leadEmail={leadEmail}
        replyTo={compose && compose.mode === "reply" ? compose.email : null}
        replyAll={!!(compose && compose.all)}
      />
    </div>
  );
}
